/**
 * Document date helpers shared by Title block UI, LML preview, and
 * bare-token expansion in paragraph static views.
 *
 * Title-block content shape:
 *   { title, author, dateMode?: "today"|"fixed", date?: string }
 * - dateMode "today" (or date empty / "\\today") → live calendar date
 * - dateMode "fixed" + date "YYYY-MM-DD" or free text → that value
 */

/** English long-form date in UTC (matches API ExpandBareLatexMetaTokens). */
/**
 * The language dates are written in on screen: the document's (20 Oct), as its PDF prints them.
 * Set by the editor from the document's language (setDocumentDateLocale); English until then.
 */
let documentDateLocale = "en-US";

export function setDocumentDateLocale(language: string | null | undefined): void {
  const code = (language || "en").split("-")[0].toLowerCase();
  if (code === "en" || code === "auto" || !/^[a-z]{2}$/.test(code)) {
    documentDateLocale = language === "en-GB" ? "en-GB" : "en-US";
    return;
  }
  // Western digits, as LaTeX prints them ("19 أكتوبر 2026"), not Arabic-Indic.
  const locale = `${code}-u-nu-latn`;
  documentDateLocale = Intl.DateTimeFormat.supportedLocalesOf([locale]).length ? locale : "en-US";
}

export function formatLongDate(now: Date = new Date()): string {
  return now.toLocaleDateString(documentDateLocale, {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Expand bare LaTeX meta tokens for non-TeX display surfaces.
 * - `\date{\today}` / `\date{X}` → resolved date (body misuse of preamble cmd)
 * - `\today` → calendar date
 * - `\and` → ", "
 */
export function expandLatexMetaTokens(text: string, now: Date = new Date()): string {
  if (!text) return text;
  const today = formatLongDate(now);
  return text
    // Preamble command dropped into body (Architect often writes this) —
    // treat like an inline date. `\date{\today}` first, then free-form.
    .replace(/\\date\{\\today\}/g, today)
    .replace(/\\date\{([^}]*)\}/g, (_m, inner: string) => {
      const t = (inner || "").trim();
      if (!t || t === "\\today") return today;
      const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
      if (iso) {
        return formatLongDate(new Date(Date.UTC(+iso[1], +iso[2] - 1, +iso[3])));
      }
      return t;
    })
    .replace(/\\today\b/g, today)
    .replace(/\s*\\and\s*/g, ", ");
}

export type DocumentDateMode = "today" | "fixed";

export function resolveDateMode(
  date: string | undefined | null,
  dateMode?: string | null,
): DocumentDateMode {
  if (dateMode === "fixed") return "fixed";
  if (dateMode === "today") return "today";
  const d = (date ?? "").trim();
  if (!d || d === "\\today") return "today";
  return "fixed";
}

/** Human-readable date for the Flow canvas / LML labels. */
export function formatDocumentDateDisplay(
  date: string | undefined | null,
  dateMode?: string | null,
  now: Date = new Date(),
): string {
  const mode = resolveDateMode(date, dateMode);
  if (mode === "today") return formatLongDate(now);
  const d = (date ?? "").trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (iso) {
    const parsed = new Date(Date.UTC(+iso[1], +iso[2] - 1, +iso[3]));
    return formatLongDate(parsed);
  }
  return expandLatexMetaTokens(d, now) || formatLongDate(now);
}

/** Value stored / emitted for LaTeX `\date{…}`. */
export function toLatexDateValue(
  date: string | undefined | null,
  dateMode?: string | null,
): string {
  const mode = resolveDateMode(date, dateMode);
  if (mode === "today") return "\\today";
  const d = (date ?? "").trim();
  if (!d) return "\\today";
  // Prefer human form for fixed ISO so LaTeX prints a nice date without packages.
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (iso) {
    const parsed = new Date(Date.UTC(+iso[1], +iso[2] - 1, +iso[3]));
    return formatLongDate(parsed);
  }
  return d;
}

/** ISO yyyy-mm-dd for <input type="date">, or today if mode is live. */
export function toDateInputValue(
  date: string | undefined | null,
  dateMode?: string | null,
  now: Date = new Date(),
): string {
  const mode = resolveDateMode(date, dateMode);
  if (mode === "today") {
    return now.toISOString().slice(0, 10);
  }
  const d = (date ?? "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  return now.toISOString().slice(0, 10);
}
